import React, { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import axios from "axios";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/use-toast";

import { cn } from "@/lib/utils";
import {
  Building2,
  FileUp,
  Mail,
  Settings as SettingsIcon,
  Wand2,
  LayoutDashboard,
  Shield,
  Table2,
  LogOut,
  Inbox,
  Star,
  StarOff,
  MailOpen,
  MailCheck,
  Search,
  RefreshCw,
  Send,
  Trash2,
  ArrowLeft,
  Reply,
  MessageSquare,
  ChevronDown,
  X,
  Paperclip,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
} from "lucide-react";

// ALWAYS use current origin for API calls - this ensures it works in any environment
// The backend is always on the same domain, so we don't need environment variables
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

function useApiData(fetcher, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    fetcher()
      .then((res) => {
        if (!mounted) return;
        setData(res);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.response?.data?.detail || e.message || "Failed");
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, setData };
}

// ----------------------
// Auth helpers
// ----------------------

function useAuth() {
  const [user, setUser] = useState(null);
  const [authState, setAuthState] = useState(null); // null checking, true authed, false not

  const refresh = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      setAuthState(true);
      return res.data;
    } catch (e) {
      setUser(null);
      setAuthState(false);
      return null;
    }
  };

  return { user, setUser, authState, setAuthState, refresh };
}

const Fullscreen = ({ children }) => {
  return (
    <div
      data-testid="fullscreen-wrapper"
      className="dark min-h-screen bg-[radial-gradient(900px_420px_at_12%_8%,rgba(99,102,241,0.18),transparent_60%),radial-gradient(900px_420px_at_88%_0%,rgba(20,184,166,0.14),transparent_62%),linear-gradient(180deg,#151627,#141523_55%,#121320)] text-zinc-100"
    >
      <div className="mx-auto flex min-h-screen max-w-7xl items-center justify-center px-4 py-10">{children}</div>
      <Toaster />
    </div>
  );
};

import stallineLogo from "@/assets/stalline-logo.png";

const LoginPage = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;

  return (
    <Fullscreen>
      <div data-testid="login-page" className="grid w-full max-w-4xl gap-6 lg:grid-cols-2">
        <Card
          data-testid="login-card"
          className="relative overflow-hidden border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.06))] backdrop-blur-2xl shadow-[0_50px_140px_-110px_rgba(168,85,247,0.95)]"
        >
          <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -right-28 -bottom-28 h-72 w-72 rounded-full bg-teal-500/16 blur-3xl" />
          <CardHeader className="pt-4 pb-2">
            <div className="flex flex-col items-center">
              <img
                data-testid="login-logo"
                src={stallineLogo}
                alt="Stalline Media"
                className="h-56 w-auto max-w-[680px] object-contain invert brightness-110 contrast-125 drop-shadow-[0_22px_52px_rgba(0,0,0,0.7)]"
              />
              <div className="mt-2 w-full text-center">
                <div data-testid="login-title" className="text-3xl font-semibold tracking-tight">
                  <span className="bg-gradient-to-b from-white via-white to-zinc-300 bg-clip-text text-transparent">
                    Stalline Outreach
                  </span>
                </div>
              </div>
              <CardDescription data-testid="login-desc" className="mt-2 text-base md:text-lg text-zinc-100 text-center">
                Private workspace for Stalline Media — import leads, generate drafts, send via Gmail, track replies.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div data-testid="login-access-note" className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 text-sm text-zinc-100">
              Access is restricted to approved team accounts.
            </div>
            <Button
              data-testid="login-google-button"
              className="w-full rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300"
              onClick={() => {
                window.location.href = authUrl;
              }}
            >
              Continue with Google
            </Button>
            <div data-testid="login-help" className="text-xs text-zinc-300">
              If your account isn’t allowed, ask an admin to add you.
            </div>
          </CardContent>
        </Card>

        <Card
          data-testid="login-right-card"
          className="relative overflow-hidden border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.06))] backdrop-blur-2xl shadow-[0_50px_140px_-115px_rgba(20,184,166,0.9)]"
        >
          <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-violet-500/14 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 -bottom-24 h-72 w-72 rounded-full bg-emerald-500/14 blur-3xl" />
          <CardHeader>
            <CardTitle data-testid="login-right-title" className="text-lg font-semibold">
              What you get
            </CardTitle>
            <CardDescription data-testid="login-right-desc" className="text-zinc-100">
              Built for consistent outbound + follow-ups.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-zinc-100">
            <ul data-testid="login-feature-list" className="space-y-2">
              <li data-testid="login-feature-1" className="rounded-lg border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
                Apollo CSV import → companies + contacts
              </li>
              <li data-testid="login-feature-2" className="rounded-lg border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
                Offers (Legion Club / Tech Verse) → AI drafts you can edit
              </li>
              <li data-testid="login-feature-3" className="rounded-lg border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
                Gmail sending + reply tracking + follow-up drafts
              </li>
              <li data-testid="login-feature-4" className="rounded-lg border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
                Daily outreach sheet (sent/no reply/follow-up status)
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Fullscreen>
  );
};

const AuthCallback = ({ onAuthed }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : "";

    const run = async () => {
      if (!sessionId) {
        navigate("/login", { replace: true });
        return;
      }
      try {
        const res = await api.post(
          "/auth/session",
          {},
          {
            headers: {
              "X-Session-ID": sessionId,
            },
          },
        );
        onAuthed(res.data.user);
        // Clear fragment
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message || "Login failed";
        toast({ title: "Login failed", description: msg, variant: "destructive" });
        navigate("/login", { replace: true });
      }
    };

    run();
  }, [navigate, onAuthed, toast]);

  return (
    <Fullscreen>
      <div data-testid="auth-callback" className="w-full max-w-xl">
        <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="auth-callback-card">
          <CardHeader>
            <CardTitle data-testid="auth-callback-title">Signing you in…</CardTitle>
            <CardDescription data-testid="auth-callback-desc" className="text-zinc-100">
              Please wait a moment.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    </Fullscreen>
  );
};

const Protected = ({ auth, children }) => {
  const location = useLocation();

  useEffect(() => {
    if (auth.authState !== null) return;
    auth.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (auth.authState === null) {
    return (
      <Fullscreen>
        <div data-testid="auth-checking" className="w-full max-w-xl">
          <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="auth-checking-card">
            <CardHeader>
              <CardTitle data-testid="auth-checking-title">Checking access…</CardTitle>
              <CardDescription data-testid="auth-checking-desc" className="text-zinc-100">
                Verifying your session.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Fullscreen>
    );
  }

  if (auth.authState === false) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// ----------------------
// App shell
// ----------------------

const Shell = ({ auth, children }) => {
  const location = useLocation();
  const { toast } = useToast();

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/import", label: "Import", icon: FileUp, testId: "nav-import" },
    { to: "/companies", label: "Companies", icon: Building2, testId: "nav-companies" },
    { to: "/outreach", label: "Outreach", icon: Mail, testId: "nav-outreach" },
    { to: "/inbox", label: "Inbox", icon: Inbox, testId: "nav-inbox" },
    { to: "/offers", label: "Offers", icon: Wand2, testId: "nav-offers" },
    { to: "/reports", label: "Daily sheet", icon: Table2, testId: "nav-reports" },
  ];

  const adminNav = [
    { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
    { to: "/admin", label: "Admin", icon: Shield, testId: "nav-admin" },
  ];

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    auth.setUser(null);
    auth.setAuthState(false);
    toast({ title: "Logged out", description: "You have been signed out." });
  };

  return (
    <div
      data-testid="app-shell"
      className="dark min-h-screen bg-[radial-gradient(900px_420px_at_12%_8%,rgba(99,102,241,0.18),transparent_60%),radial-gradient(900px_420px_at_88%_0%,rgba(20,184,166,0.14),transparent_62%),linear-gradient(180deg,#151627,#141523_55%,#121320)] text-zinc-100"
    >
      <div className="relative mx-auto grid max-w-[1600px] grid-cols-12 gap-4 px-4 py-4 lg:px-6">
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <div className="sticky top-4">
            <div className="rounded-2xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3 shadow-[0_20px_70px_-40px_rgba(0,0,0,0.8)]">
              <div className="space-y-1">
                <div>
                  <div className="flex justify-center">
                    <img
                      data-testid="sidebar-logo"
                      src={stallineLogo}
                      alt="Stalline Media"
                      className="h-28 w-auto max-w-[200px] object-contain invert brightness-110 contrast-125 drop-shadow-[0_18px_44px_rgba(0,0,0,0.65)]"
                    />
                  </div>
                  <div className="flex justify-center">
                    <Badge data-testid="brand-badge" variant="secondary" className="bg-white/20 text-zinc-100 border-white/25 text-xs">
                      Private
                    </Badge>
                  </div>
                </div>

                <div className="text-center">
                  <div data-testid="brand-title" className="text-base font-semibold tracking-tight leading-tight">
                    Stalline Outreach
                  </div>
                </div>
              </div>

              <div className="mt-2 rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-2">
                <div>
                  <div data-testid="user-name" className="text-sm font-medium text-zinc-100">
                    {auth.user?.name || auth.user?.email}
                  </div>
                  <div data-testid="user-email" className="text-xs text-zinc-300">
                    {auth.user?.email}
                  </div>
                </div>
              </div>

              <Separator className="my-3 bg-white/20" />
              <nav data-testid="sidebar-nav" className="space-y-0.5">
                {nav.map((item) => {
                  const active = location.pathname.startsWith(item.to);
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={item.testId}
                      className={cn(
                        "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors",
                        active ? "bg-[radial-gradient(800px_220px_at_20%_0%,rgba(99,102,241,0.34),transparent_55%),linear-gradient(90deg,rgba(99,102,241,0.22),rgba(14,165,233,0.16),rgba(20,184,166,0.18))] text-white border border-white/25 shadow-[0_18px_55px_-42px_rgba(45,212,191,0.85)]" : "text-zinc-100 hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] hover:text-white",
                      )}
                    >
                      <Icon className={cn("h-4 w-4", active ? "text-indigo-200" : "text-zinc-300 group-hover:text-zinc-100")} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}

                {auth.user?.role === "admin" ? (
                  <div className="pt-2">
                    <div data-testid="admin-nav-label" className="px-3 pb-2 text-xs font-semibold text-zinc-300">
                      Admin
                    </div>
                    {adminNav.map((item) => {
                      const active = location.pathname.startsWith(item.to);
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          data-testid={item.testId}
                          className={cn(
                            "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors",
                            active ? "bg-[radial-gradient(800px_220px_at_20%_0%,rgba(99,102,241,0.34),transparent_55%),linear-gradient(90deg,rgba(99,102,241,0.22),rgba(14,165,233,0.16),rgba(20,184,166,0.18))] text-white border border-white/25 shadow-[0_18px_55px_-42px_rgba(45,212,191,0.85)]" : "text-zinc-100 hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] hover:text-white",
                          )}
                        >
                          <Icon className={cn("h-4 w-4", active ? "text-indigo-200" : "text-zinc-300 group-hover:text-zinc-100")} />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </nav>

              <div className="mt-3 rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-2">
                <Button
                  data-testid="logout-button"
                  variant="secondary"
                  className="w-full justify-center gap-2 rounded-xl bg-white/20 px-2 py-1.5 text-zinc-100 hover:bg-white/[0.18]"
                  onClick={logout}
                >
                  <LogOut className="h-4 w-4" />
                  <span data-testid="logout-button-text" className="text-sm">Logout</span>
                </Button>
              </div>

            </div>

            <div className="mt-3 rounded-2xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
              <div data-testid="quick-tip-title" className="text-xs font-semibold text-zinc-100">
                Quick tip
              </div>
              <div data-testid="quick-tip-text" className="mt-1 text-xs leading-relaxed text-zinc-100">
                Campaign check is manual: add Instagram/YouTube URLs + notes on each company.
              </div>
            </div>
          </div>
        </aside>

        <main className="col-span-12 md:col-span-9 lg:col-span-10">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute left-1/2 top-10 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-indigo-500/10 blur-3xl" />
            <div className="absolute right-12 top-28 h-[420px] w-[420px] rounded-full bg-teal-500/10 blur-3xl" />
          </div>
          <div className="rounded-2xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.05))] backdrop-blur-2xl shadow-[0_24px_80px_-55px_rgba(0,0,0,0.85)]">
            <div className="border-b border-white/25 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div data-testid="page-title" className="text-lg font-semibold tracking-tight">
                    {location.pathname === "/dashboard"
                      ? "Dashboard"
                      : location.pathname.replace("/", "").split("/")[0].toUpperCase()}
                  </div>
                  <div data-testid="page-subtitle" className="text-sm text-zinc-100">
                    Import leads → generate drafts → review → send → follow up
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4">{children}</div>
          </div>
        </main>
      </div>
      <Toaster />
    </div>
  );
};

// ----------------------
// Pages
// ----------------------

const DashboardPage = () => {
  const { data: companies } = useApiData(() => api.get("/companies").then((r) => r.data), []);
  const { data: drafts } = useApiData(() => api.get("/outreach/drafts").then((r) => r.data), []);
  const { data: sent } = useApiData(() => api.get("/outreach/sent").then((r) => r.data), []);
  const { data: replied } = useApiData(() => api.get("/outreach/replied").then((r) => r.data), []);

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard testId="stat-companies" title="Companies" value={(companies || []).length} hint="Imported + manual" />
        <StatCard testId="stat-drafts" title="Drafts" value={(drafts || []).length} hint="Ready to review" />
        <StatCard testId="stat-sent" title="Sent" value={(sent || []).filter((x) => x.status === "SENT").length} hint="Delivered via Gmail" />
        <StatCard testId="stat-replied" title="Replied" value={(replied || []).length} hint="Detected in threads" />
      </div>

      <Card className="relative overflow-hidden border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.13),rgba(255,255,255,0.05))] backdrop-blur-2xl shadow-[0_28px_90px_-70px_rgba(0,0,0,0.85)]" data-testid="dashboard-next-steps-card">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-indigo-400/70 via-sky-400/55 to-teal-300/65" />
        <div aria-hidden="true" className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-indigo-500/12 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 -bottom-24 h-72 w-72 rounded-full bg-teal-500/10 blur-3xl" />
        <CardHeader>
          <CardTitle data-testid="dashboard-next-steps-title">Next steps</CardTitle>
          <CardDescription data-testid="dashboard-next-steps-desc" className="text-zinc-100">
            Recommended flow for pitching Stalline Media IPs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <StepRow testId="step-1" title="1) Upload Apollo CSV" desc="Import companies + marketing contacts." to="/import" gradient="violet" />
          <StepRow testId="step-2" title="2) Add social URLs + campaign notes" desc="Manual campaign tracking in MVP." to="/companies" gradient="blue" />
          <StepRow testId="step-3" title="3) Upload your deck PDF" desc="Will attach automatically when sending." to="/settings" gradient="teal" />
          <StepRow testId="step-4" title="4) Generate drafts + review" desc="Edit subject/body before sending." to="/outreach" gradient="orange" />
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({ testId, title, value, hint }) => {
  return (
    <div data-testid={testId} className="rounded-2xl border border-white/25 bg-[radial-gradient(900px_220px_at_20%_0%,rgba(99,102,241,0.28),transparent_55%),linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.04))] p-4 shadow-[0_18px_55px_-45px_rgba(20,184,166,0.55)]">
      <div data-testid={`${testId}-title`} className="text-sm text-zinc-100">
        {title}
      </div>
      <div data-testid={`${testId}-value`} className="mt-2 text-3xl font-semibold tracking-tight">
        {value}
      </div>
      <div data-testid={`${testId}-hint`} className="mt-1 text-sm text-zinc-300">
        {hint}
      </div>
    </div>
  );
};

const StepRow = ({ testId, title, desc, to, gradient }) => {
  const gradientStyles = {
    purple: "bg-gradient-to-r from-purple-600/40 via-purple-500/25 to-indigo-600/30 border-purple-400/40 hover:from-purple-600/50 hover:via-purple-500/35 hover:to-indigo-600/40",
    blue: "bg-gradient-to-r from-blue-600/40 via-cyan-500/25 to-sky-600/30 border-blue-400/40 hover:from-blue-600/50 hover:via-cyan-500/35 hover:to-sky-600/40",
    teal: "bg-gradient-to-r from-teal-600/40 via-emerald-500/25 to-green-600/30 border-teal-400/40 hover:from-teal-600/50 hover:via-emerald-500/35 hover:to-green-600/40",
    orange: "bg-gradient-to-r from-orange-600/40 via-amber-500/25 to-yellow-600/30 border-orange-400/40 hover:from-orange-600/50 hover:via-amber-500/35 hover:to-yellow-600/40",
    slate: "bg-gradient-to-r from-slate-600/50 via-gray-500/35 to-zinc-600/40 border-slate-400/40 hover:from-slate-500/60 hover:via-gray-400/45 hover:to-zinc-500/50",
    violet: "bg-gradient-to-r from-violet-600/40 via-purple-500/25 to-fuchsia-600/30 border-violet-400/40 hover:from-violet-600/50 hover:via-purple-500/35 hover:to-fuchsia-600/40",
  };
  const gradientClass = gradientStyles[gradient] || gradientStyles.purple;
  
  return (
    <Link data-testid={testId} to={to} className={`block rounded-xl border backdrop-blur-xl shadow-lg p-4 transition-all duration-300 ${gradientClass}`}>
      <div data-testid={`${testId}-title`} className="font-semibold text-white">
        {title}
      </div>
      <div data-testid={`${testId}-desc`} className="mt-1 text-sm text-zinc-200">
        {desc}
      </div>
    </Link>
  );
};

const ImportPage = () => {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Manual add (non-Apollo)
  const [manual, setManual] = useState({ company: "", first_name: "", last_name: "", email: "", phone: "" });
  const [manualBusy, setManualBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/import/apollo-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast({ title: "Import complete", description: "Your Apollo CSV was processed." });
    } catch (e) {
      toast({ title: "Import failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const addManualLead = async () => {
    if (!manual.company.trim() || !manual.email.trim()) return;
    setManualBusy(true);
    try {
      // Create company
      const companyRes = await api.post("/companies", {
        name: manual.company.trim(),
        website: "",
        socials: {},
        campaign_notes: "",
      });

      // Create contact
      await api.post("/contacts", {
        company_id: companyRes.data.id,
        first_name: manual.first_name.trim(),
        last_name: manual.last_name.trim(),
        title: "",
        email: manual.email.trim(),
        phone: manual.phone.trim(),
        linkedin: "",
      });

      toast({ title: "Added", description: "Company + contact added." });
      setManual({ company: "", first_name: "", last_name: "", email: "", phone: "" });
    } catch (e) {
      toast({ title: "Add failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setManualBusy(false);
    }
  };

  return (
    <div data-testid="import-page" className="space-y-6">
      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="import-card">
        <CardHeader>
          <CardTitle data-testid="import-title">Upload Apollo export (CSV)</CardTitle>
          <CardDescription data-testid="import-desc" className="text-zinc-100">
            Export leads from Apollo → upload here. We will import companies + contacts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input data-testid="import-file-input" type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="import-upload-button"
              onClick={upload}
              disabled={!file || busy}
              className="rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300"
            >
              {busy ? "Uploading…" : "Import CSV"}
            </Button>
            {file ? (
              <Badge data-testid="import-file-badge" variant="secondary" className="bg-white/20 text-zinc-100 border-white/25">
                {file.name}
              </Badge>
            ) : null}
          </div>

          {result ? (
            <div data-testid="import-result" className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 text-sm text-zinc-100">
              <div data-testid="import-result-created-companies">Created companies: {result.created_companies}</div>
              <div data-testid="import-result-created-contacts">Created contacts: {result.created_contacts}</div>
              <div data-testid="import-result-updated-contacts">Updated contacts: {result.updated_contacts}</div>
              {(result.errors || []).length ? (
                <div className="mt-2">
                  <div data-testid="import-errors-title" className="font-medium">
                    Errors (first {Math.min(result.errors.length, 50)})
                  </div>
                  <ul data-testid="import-errors-list" className="mt-1 list-disc pl-5 text-zinc-100">
                    {result.errors.map((er, i) => (
                      <li data-testid={`import-error-${i}`} key={i}>
                        {er}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="manual-lead-card">
        <CardHeader>
          <CardTitle data-testid="manual-lead-title">Add a lead manually</CardTitle>
          <CardDescription data-testid="manual-lead-desc" className="text-zinc-100">
            Use this when you get leads from sources other than Apollo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <div data-testid="manual-company-label" className="text-sm text-zinc-100">
                Company name
              </div>
              <Input
                data-testid="manual-company-input"
                value={manual.company}
                onChange={(e) => setManual({ ...manual, company: e.target.value })}
                className="bg-white/5 border-white/25"
                placeholder="Example: Acme Inc"
              />
            </div>
            <div>
              <div data-testid="manual-email-label" className="text-sm text-zinc-100">
                Person email
              </div>
              <Input
                data-testid="manual-email-input"
                value={manual.email}
                onChange={(e) => setManual({ ...manual, email: e.target.value })}
                className="bg-white/5 border-white/25"
                placeholder="name@company.com"
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <div data-testid="manual-first-name-label" className="text-sm text-zinc-100">
                Person first name
              </div>
              <Input
                data-testid="manual-first-name-input"
                value={manual.first_name}
                onChange={(e) => setManual({ ...manual, first_name: e.target.value })}
                className="bg-white/5 border-white/25"
                placeholder="First name"
              />
            </div>
            <div>
              <div data-testid="manual-last-name-label" className="text-sm text-zinc-100">
                Person last name
              </div>
              <Input
                data-testid="manual-last-name-input"
                value={manual.last_name}
                onChange={(e) => setManual({ ...manual, last_name: e.target.value })}
                className="bg-white/5 border-white/25"
                placeholder="Last name"
              />
            </div>
          </div>

          <div>
            <div data-testid="manual-phone-label" className="text-sm text-zinc-100">
              Contact number (optional)
            </div>
            <Input
              data-testid="manual-phone-input"
              value={manual.phone}
              onChange={(e) => setManual({ ...manual, phone: e.target.value })}
              className="bg-white/5 border-white/25"
              placeholder="+91…"
            />
          </div>

          <Button
            data-testid="manual-add-button"
            onClick={addManualLead}
            disabled={manualBusy || !manual.company.trim() || !manual.email.trim()}
            className="rounded-xl bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-[#041014] shadow-[0_18px_55px_-38px_rgba(45,212,191,0.70)] hover:from-emerald-300 hover:via-teal-300 hover:to-cyan-300"
          >
            {manualBusy ? "Adding…" : "Add lead"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="import-help-card">
        <CardHeader>
          <CardTitle data-testid="import-help-title">What columns do you need?</CardTitle>
          <CardDescription data-testid="import-help-desc" className="text-zinc-100">
            Apollo exports vary; we only require **Company** and **Email**. Extra fields will be used if present.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};

const CompaniesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data, loading, error, setData } = useApiData(() => api.get("/companies").then((r) => r.data), []);
  const [deletingId, setDeletingId] = useState(null);

  const deleteCompany = async (e, companyId) => {
    e.stopPropagation();
    setDeletingId(companyId);
    
    // Wait for shake animation
    await new Promise(r => setTimeout(r, 300));
    
    try {
      await api.delete(`/companies/${companyId}`);
      // Wait for fade out animation
      await new Promise(r => setTimeout(r, 400));
      setData((prev) => (prev || []).filter((c) => c.id !== companyId));
      toast({ title: "Deleted", description: "Company and contacts removed." });
    } catch (err) {
      console.error("Delete company error:", err);
      toast({ title: "Delete failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid="companies-page" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div data-testid="companies-title" className="text-lg font-semibold">Companies</div>
          <div data-testid="companies-subtitle" className="text-sm text-zinc-100">Add social URLs + campaign notes for manual tracking.</div>
        </div>
        <Button data-testid="companies-refresh-button" variant="secondary" className="rounded-xl bg-white/20 text-zinc-100 hover:bg-white/[0.18]" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </div>

      {loading ? <div data-testid="companies-loading" className="text-sm text-zinc-100">Loading…</div> : null}
      {error ? <div data-testid="companies-error" className="text-sm text-rose-200">{error}</div> : null}

      <div className="grid gap-3 md:grid-cols-2">
        {(data || []).map((c) => (
          <div
            key={c.id}
            data-testid={`company-card-${c.id}`}
            className={cn(
              "text-left rounded-2xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 transition-all hover:bg-white/[0.10]",
              deletingId === c.id && "delete-removing"
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <button onClick={() => navigate(`/companies/${c.id}`)} className="text-left flex-1">
                <div data-testid={`company-card-name-${c.id}`} className="font-semibold text-zinc-100">{c.name}</div>
                <div data-testid={`company-card-website-${c.id}`} className="mt-1 text-sm text-zinc-100">{c.website || "—"}</div>
              </button>
              <Button 
                data-testid={`company-delete-${c.id}`}
                type="button"
                variant="secondary" 
                size="sm"
                className="delete-btn bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 rounded-lg text-xs px-2 py-1"
                onClick={(e) => deleteCompany(e, c.id)}
                disabled={deletingId === c.id}
              >
                {deletingId === c.id ? "..." : "Delete"}
              </Button>
            </div>
            <button onClick={() => navigate(`/companies/${c.id}`)} className="text-left w-full">
              <div className="mt-3 text-sm text-zinc-300 line-clamp-2">{c.campaign_notes || "No campaign notes yet."}</div>
            </button>
          </div>
        ))}
      </div>

      {!loading && (data || []).length === 0 ? (
        <div data-testid="companies-empty" className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 text-sm text-zinc-100">
          No companies yet. Go to Import to upload an Apollo CSV.
        </div>
      ) : null}
    </div>
  );
};

const CompanyDetailPage = () => {
  const { toast } = useToast();
  const { companyId } = useParams();
  const { data: company, loading: cLoading, error: cError, setData: setCompany } = useApiData(() => api.get(`/companies/${companyId}`).then((r) => r.data), [companyId]);
  const { data: contacts, loading: ctLoading, error: ctError, setData: setContacts } = useApiData(() => api.get(`/contacts?company_id=${companyId}`).then((r) => r.data), [companyId]);

  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState(null);

  useEffect(() => {
    if (company) setLocal(company);
  }, [company]);

  const save = async () => {
    if (!local) return;
    setSaving(true);
    try {
      const res = await api.put(`/companies/${companyId}`, {
        website: local.website,
        campaign_notes: local.campaign_notes,
        socials: local.socials,
      });
      setCompany(res.data);
      toast({ title: "Saved", description: "Company details updated." });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const removeContact = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      setContacts((prev) => (prev || []).filter((c) => c.id !== id));
      toast({ title: "Deleted", description: "Contact removed." });
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  return (
    <div data-testid="company-detail-page" className="space-y-6">
      {cLoading ? <div data-testid="company-detail-loading" className="text-sm text-zinc-100">Loading…</div> : null}
      {cError ? <div data-testid="company-detail-error" className="text-sm text-rose-200">{cError}</div> : null}

      {local ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] lg:col-span-2" data-testid="company-detail-card">
            <CardHeader>
              <CardTitle data-testid="company-detail-name">{local.name}</CardTitle>
              <CardDescription data-testid="company-detail-desc" className="text-zinc-100">
                Social URLs + campaign notes (manual).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div data-testid="company-website-label" className="text-sm text-zinc-100">Website</div>
                <Input data-testid="company-website-input" value={local.website || ""} onChange={(e) => setLocal({ ...local, website: e.target.value })} className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25" />
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <SocialField label="Instagram" testId="company-instagram" value={local.socials?.instagram || ""} onChange={(v) => setLocal({ ...local, socials: { ...local.socials, instagram: v } })} />
                <SocialField label="YouTube" testId="company-youtube" value={local.socials?.youtube || ""} onChange={(v) => setLocal({ ...local, socials: { ...local.socials, youtube: v } })} />
                <SocialField label="LinkedIn" testId="company-linkedin" value={local.socials?.linkedin || ""} onChange={(v) => setLocal({ ...local, socials: { ...local.socials, linkedin: v } })} />
                <SocialField label="TikTok" testId="company-tiktok" value={local.socials?.tiktok || ""} onChange={(v) => setLocal({ ...local, socials: { ...local.socials, tiktok: v } })} />
              </div>

              <div>
                <div data-testid="company-notes-label" className="text-sm text-zinc-100">Campaign notes</div>
                <Textarea
                  data-testid="company-notes-input"
                  value={local.campaign_notes || ""}
                  onChange={(e) => setLocal({ ...local, campaign_notes: e.target.value })}
                  className="min-h-[140px] bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25"
                  placeholder="Example: Running a new influencer push on Instagram for product launch — saw 3 reels in last 7 days."
                />
              </div>

              <Button data-testid="company-save-button" onClick={save} disabled={saving} className="rounded-xl bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-[#041014] shadow-[0_18px_55px_-38px_rgba(45,212,191,0.70)] hover:from-emerald-300 hover:via-teal-300 hover:to-cyan-300">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </CardContent>
          </Card>

          <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="company-contacts-card">
            <CardHeader>
              <CardTitle data-testid="company-contacts-title">Contacts</CardTitle>
              <CardDescription data-testid="company-contacts-desc" className="text-zinc-100">
                Imported from Apollo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {ctLoading ? <div data-testid="company-contacts-loading" className="text-sm text-zinc-100">Loading…</div> : null}
              {ctError ? <div data-testid="company-contacts-error" className="text-sm text-rose-200">{ctError}</div> : null}

              <div className="space-y-2">
                {(contacts || []).map((ct) => (
                  <div key={ct.id} data-testid={`company-contact-${ct.id}`} className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
                    <div data-testid={`company-contact-name-${ct.id}`} className="font-medium text-zinc-100">
                      {(ct.first_name || "").trim()} {(ct.last_name || "").trim()}
                    </div>
                    <div data-testid={`company-contact-title-${ct.id}`} className="text-sm text-zinc-100">{ct.title || "—"}</div>
                    <div data-testid={`company-contact-email-${ct.id}`} className="text-sm text-zinc-300">{ct.email}</div>
                    <div className="mt-2 flex gap-2">
                      <Button
                        data-testid={`company-contact-delete-${ct.id}`}
                        variant="secondary"
                        className="h-8 rounded-lg bg-white/20 text-zinc-100 hover:bg-white/[0.18]"
                        onClick={() => removeContact(ct.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {!ctLoading && (contacts || []).length === 0 ? (
                <div data-testid="company-contacts-empty" className="text-sm text-zinc-100">No contacts found for this company.</div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};

const SocialField = ({ label, testId, value, onChange }) => {
  return (
    <div>
      <div data-testid={`${testId}-label`} className="text-sm text-zinc-100">{label}</div>
      <Input data-testid={`${testId}-input`} value={value} onChange={(e) => onChange(e.target.value)} className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25" placeholder={`Paste ${label} URL`} />
    </div>
  );
};

const OffersPage = () => {
  const { toast } = useToast();
  const { data, loading, error, setData } = useApiData(() => api.get("/offers").then((r) => r.data), []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ 
    name: "", 
    about: "", 
    proof_points: "", 
    cta_text: "", 
    signature: "Regards,\nStalline Media", 
    brand: "Stalline Media",
    use_ai: true,
    subject_template: "",
    email_template: ""
  });

  const create = async () => {
    try {
      const res = await api.post("/offers", form);
      setData([res.data, ...(data || [])]);
      setOpen(false);
      setForm({ 
        name: "", 
        about: "", 
        proof_points: "", 
        cta_text: "", 
        signature: "Regards,\nStalline Media", 
        brand: "Stalline Media",
        use_ai: true,
        subject_template: "",
        email_template: ""
      });
      toast({ title: "Offer created", description: form.use_ai ? "AI will generate emails using this pitch." : "Your manual template will be used as-is." });
    } catch (e) {
      toast({ title: "Create failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const update = async (offerId, patch) => {
    try {
      const res = await api.put(`/offers/${offerId}`, patch);
      setData((prev) => (prev || []).map((o) => (o.id === offerId ? res.data : o)));
      toast({ title: "Saved", description: "Offer updated." });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const remove = async (offerId) => {
    try {
      await api.delete(`/offers/${offerId}`);
      setData((prev) => (prev || []).filter((o) => o.id !== offerId));
      toast({ title: "Deleted", description: "Offer removed." });
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  return (
    <div data-testid="offers-page" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div data-testid="offers-title" className="text-lg font-semibold">Offers (pitch profiles)</div>
          <div data-testid="offers-subtitle" className="text-sm text-zinc-100">Keep multiple IP pitches and swap anytime.</div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="offer-new-button" className="rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300">New offer</Button>
          </DialogTrigger>
          <DialogContent data-testid="offer-new-dialog" className="border-white/25 bg-[#0b0c18] text-zinc-100 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="offer-new-title">Create offer</DialogTitle>
              <DialogDescription data-testid="offer-new-desc" className="text-zinc-100">
                Choose AI-generated or write your own email template.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input data-testid="offer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Offer name" className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25" />
              
              {/* Toggle for AI vs Manual */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, use_ai: true })}
                  className={cn(
                    "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all",
                    form.use_ai 
                      ? "bg-indigo-500 text-white" 
                      : "bg-white/10 text-zinc-400 hover:bg-white/15"
                  )}
                >
                  🤖 AI Generated
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, use_ai: false })}
                  className={cn(
                    "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all",
                    !form.use_ai 
                      ? "bg-emerald-500 text-white" 
                      : "bg-white/10 text-zinc-400 hover:bg-white/15"
                  )}
                >
                  ✍️ Manual Template
                </button>
              </div>

              {form.use_ai ? (
                <>
                  {/* AI mode fields */}
                  <Textarea data-testid="offer-about-input" value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} placeholder="About (what you do)" className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[110px]" />
                  <Textarea data-testid="offer-proof-input" value={form.proof_points} onChange={(e) => setForm({ ...form, proof_points: e.target.value })} placeholder="Proof points (followers, reach, examples)" className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[110px]" />
                  <Textarea data-testid="offer-cta-input" value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} placeholder="CTA text" className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[80px]" />
                  <Textarea data-testid="offer-signature-input" value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} placeholder="Signature" className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[80px]" />
                </>
              ) : (
                <>
                  {/* Manual template mode fields */}
                  <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
                    <strong>Placeholders:</strong> {"{first_name}"}, {"{last_name}"}, {"{company}"}, {"{title}"}, {"{email}"}
                  </div>
                  <div>
                    <div className="text-sm text-zinc-100 mb-1">Email Subject</div>
                    <Input 
                      data-testid="offer-subject-input" 
                      value={form.subject_template} 
                      onChange={(e) => setForm({ ...form, subject_template: e.target.value })} 
                      placeholder="e.g., Quick question for {first_name}" 
                      className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25" 
                    />
                  </div>
                  <div>
                    <div className="text-sm text-zinc-100 mb-1">Email Body</div>
                    <Textarea 
                      data-testid="offer-email-template-input" 
                      value={form.email_template} 
                      onChange={(e) => setForm({ ...form, email_template: e.target.value })} 
                      placeholder={`Hi {first_name},\n\nI came across {company} and loved what you're doing...\n\nWould love to connect!\n\nBest,\nYour Name`}
                      className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[200px] font-mono text-sm" 
                    />
                  </div>
                </>
              )}
              
              <Button data-testid="offer-create-submit" onClick={create} disabled={!form.name.trim() || (!form.use_ai && (!form.subject_template.trim() || !form.email_template.trim()))} className="rounded-xl bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-[#041014] shadow-[0_18px_55px_-38px_rgba(45,212,191,0.70)] hover:from-emerald-300 hover:via-teal-300 hover:to-cyan-300">
                Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div data-testid="offers-loading" className="text-sm text-zinc-100">Loading…</div> : null}
      {error ? <div data-testid="offers-error" className="text-sm text-rose-200">{error}</div> : null}

      <div className="grid gap-3">
        {(data || []).map((o) => (
          <OfferCard
            key={`${o.id}:${o.updated_at || ""}`}
            offer={o}
            onUpdate={(patch) => update(o.id, patch)}
            onDelete={() => remove(o.id)}
          />
        ))}
      </div>
    </div>
  );
};

const OfferCard = ({ offer, onUpdate, onDelete }) => {
  const [local, setLocal] = useState(offer);
  const isManual = !local.use_ai;
  
  return (
    <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid={`offer-card-${offer.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle data-testid={`offer-card-title-${offer.id}`} className="flex items-center gap-2">
              {offer.name}
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full",
                isManual ? "bg-emerald-500/20 text-emerald-300" : "bg-indigo-500/20 text-indigo-300"
              )}>
                {isManual ? "Manual" : "AI"}
              </span>
            </CardTitle>
            <CardDescription data-testid={`offer-card-brand-${offer.id}`} className="text-zinc-100">
              {offer.brand}
            </CardDescription>
          </div>
          <Button data-testid={`offer-delete-${offer.id}`} variant="secondary" className="rounded-xl bg-white/20 text-zinc-100 hover:bg-white/[0.18]" onClick={onDelete}>
            Delete
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Toggle for AI vs Manual */}
        <div className="flex items-center gap-3 p-2 rounded-lg bg-white/5 border border-white/10">
          <button
            type="button"
            onClick={() => setLocal({ ...local, use_ai: true })}
            className={cn(
              "flex-1 py-1.5 px-3 rounded-lg text-xs font-medium transition-all",
              local.use_ai 
                ? "bg-indigo-500 text-white" 
                : "bg-white/10 text-zinc-400 hover:bg-white/15"
            )}
          >
            🤖 AI Generated
          </button>
          <button
            type="button"
            onClick={() => setLocal({ ...local, use_ai: false })}
            className={cn(
              "flex-1 py-1.5 px-3 rounded-lg text-xs font-medium transition-all",
              !local.use_ai 
                ? "bg-emerald-500 text-white" 
                : "bg-white/10 text-zinc-400 hover:bg-white/15"
            )}
          >
            ✍️ Manual Template
          </button>
        </div>

        {local.use_ai ? (
          <>
            {/* AI mode fields */}
            <div>
              <div data-testid={`offer-about-label-${offer.id}`} className="text-sm text-zinc-100">About</div>
              <Textarea data-testid={`offer-about-input-${offer.id}`} value={local.about || ""} onChange={(e) => setLocal({ ...local, about: e.target.value })} className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[110px]" />
            </div>
            <div>
              <div data-testid={`offer-proof-label-${offer.id}`} className="text-sm text-zinc-100">Proof points</div>
              <Textarea data-testid={`offer-proof-input-${offer.id}`} value={local.proof_points || ""} onChange={(e) => setLocal({ ...local, proof_points: e.target.value })} className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[110px]" />
            </div>
            <div>
              <div data-testid={`offer-cta-label-${offer.id}`} className="text-sm text-zinc-100">CTA</div>
              <Textarea data-testid={`offer-cta-input-${offer.id}`} value={local.cta_text || ""} onChange={(e) => setLocal({ ...local, cta_text: e.target.value })} className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[80px]" />
            </div>
            <div>
              <div data-testid={`offer-signature-label-${offer.id}`} className="text-sm text-zinc-100">Signature</div>
              <Textarea data-testid={`offer-signature-input-${offer.id}`} value={local.signature || ""} onChange={(e) => setLocal({ ...local, signature: e.target.value })} className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[80px]" />
            </div>
          </>
        ) : (
          <>
            {/* Manual template mode fields */}
            <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
              <strong>Placeholders:</strong> {"{first_name}"}, {"{last_name}"}, {"{company}"}, {"{title}"}, {"{email}"}
            </div>
            <div>
              <div className="text-sm text-zinc-100 mb-1">Email Subject</div>
              <Input 
                data-testid={`offer-subject-input-${offer.id}`}
                value={local.subject_template || ""} 
                onChange={(e) => setLocal({ ...local, subject_template: e.target.value })} 
                placeholder="e.g., Quick question for {first_name}" 
                className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25" 
              />
            </div>
            <div>
              <div className="text-sm text-zinc-100 mb-1">Email Body</div>
              <Textarea 
                data-testid={`offer-email-template-input-${offer.id}`}
                value={local.email_template || ""} 
                onChange={(e) => setLocal({ ...local, email_template: e.target.value })} 
                placeholder={`Hi {first_name},\n\nI came across {company} and loved what you're doing...\n\nWould love to connect!\n\nBest,\nYour Name`}
                className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[200px] font-mono text-sm" 
              />
            </div>
          </>
        )}
        
        <Button 
          data-testid={`offer-save-${offer.id}`} 
          onClick={() => onUpdate({ 
            use_ai: local.use_ai,
            about: local.about, 
            proof_points: local.proof_points, 
            cta_text: local.cta_text, 
            signature: local.signature,
            subject_template: local.subject_template,
            email_template: local.email_template
          })} 
          className="rounded-xl bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-[#041014] shadow-[0_18px_55px_-38px_rgba(45,212,191,0.70)] hover:from-emerald-300 hover:via-teal-300 hover:to-cyan-300"
        >
          Save
        </Button>
      </CardContent>
    </Card>
  );
};

const OutreachPage = () => {
  const { toast } = useToast();
  const { data: offers } = useApiData(() => api.get("/offers").then((r) => r.data), []);
  const { data: contacts, setData: setContacts } = useApiData(() => api.get("/contacts").then((r) => r.data), []);
  const { data: companies } = useApiData(() => api.get("/companies").then((r) => r.data), []);
  const { data: gmailStatus, setData: setGmailStatus } = useApiData(() => api.get("/integrations/gmail/status").then((r) => r.data), []);

  const { data: drafts, loading: dLoading, error: dError, setData: setDrafts } = useApiData(() => api.get("/outreach/drafts").then((r) => r.data), []);
  const { data: sent, setData: setSent } = useApiData(() => api.get("/outreach/sent").then((r) => r.data), []);

  const [offerId, setOfferId] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [deletingContacts, setDeletingContacts] = useState(false);
  const [deletingSentId, setDeletingSentId] = useState(null);

  const contactsEnriched = useMemo(() => {
    const compMap = new Map((companies || []).map((c) => [c.id, c]));
    return (contacts || []).map((ct) => ({ ...ct, company: compMap.get(ct.company_id) }));
  }, [contacts, companies]);

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const generateDrafts = async () => {
    if (!offerId || selectedIds.length === 0) return;
    setBusy(true);
    try {
      const res = await api.post("/outreach/prepare", {
        offer_id: offerId,
        contact_ids: selectedIds,
        extra_notes: extraNotes,
      });
      toast({ title: "Drafts created", description: `Created ${res.data.created}, skipped ${res.data.skipped}.` });
      const newDrafts = await api.get("/outreach/drafts").then((r) => r.data);
      setDrafts(newDrafts);
      setSelected({});
    } catch (e) {
      toast({ title: "Draft generation failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async (emailId, patch) => {
    try {
      const res = await api.put(`/outreach/${emailId}`, patch);
      setDrafts((prev) => (prev || []).map((d) => (d.id === emailId ? res.data : d)));
      toast({ title: "Saved", description: "Draft updated." });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const sendDraft = async (emailId) => {
    try {
      const res = await api.post(`/outreach/${emailId}/send`);
      toast({ title: "Sent", description: "Email sent via Gmail." });
      const newDrafts = await api.get("/outreach/drafts").then((r) => r.data);
      const newSent = await api.get("/outreach/sent").then((r) => r.data);
      setDrafts(newDrafts);
      setSent(newSent);
      return res.data;
    } catch (e) {
      toast({ title: "Send failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
      return null;
    }
  };

  const pollReplies = async () => {
    try {
      await api.post("/jobs/poll-replies");
      toast({ title: "Checked replies", description: "Reply polling job ran." });
      const newSent = await api.get("/outreach/sent").then((r) => r.data);
      setSent(newSent);
    } catch (e) {
      toast({ title: "Poll failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const generateFollowups = async () => {
    try {
      await api.post("/jobs/generate-followups");
      toast({ title: "Follow-ups generated", description: "Any due follow-up drafts are now in Drafts." });
      const newDrafts = await api.get("/outreach/drafts").then((r) => r.data);
      setDrafts(newDrafts);
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const deleteDraft = async (emailId) => {
    console.log("deleteDraft called with id:", emailId);
    try {
      const response = await api.delete(`/outreach/${emailId}`);
      console.log("Delete draft response:", response);
      setDrafts((prev) => (prev || []).filter((d) => d.id !== emailId));
      toast({ title: "Deleted", description: "Draft removed." });
    } catch (e) {
      console.error("Delete draft error:", e);
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const deleteSent = async (emailId) => {
    setDeletingSentId(emailId);
    await new Promise(r => setTimeout(r, 300));
    try {
      await api.delete(`/outreach/${emailId}`);
      await new Promise(r => setTimeout(r, 400));
      setSent((prev) => (prev || []).filter((s) => s.id !== emailId));
      toast({ title: "Deleted", description: "Sent email record removed." });
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setDeletingSentId(null);
    }
  };

  const deleteSelectedContacts = async () => {
    if (selectedIds.length === 0) return;
    setDeletingContacts(true);
    await new Promise(r => setTimeout(r, 300));
    try {
      // Delete contacts in parallel
      await Promise.all(selectedIds.map((id) => api.delete(`/contacts/${id}`)));
      await new Promise(r => setTimeout(r, 400));
      // Update contacts list by filtering out deleted ones
      setContacts((prev) => (prev || []).filter((c) => !selectedIds.includes(c.id)));
      setSelected({});
      toast({ title: "Deleted", description: `${selectedIds.length} contact(s) removed.` });
    } catch (e) {
      console.error("Delete contacts error:", e);
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setDeletingContacts(false);
    }
  };

  return (
    <div data-testid="outreach-page" className="space-y-6">
      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="outreach-gmail-card">
        <CardHeader>
          <CardTitle data-testid="outreach-gmail-title">Gmail connection</CardTitle>
          <CardDescription data-testid="outreach-gmail-desc" className="text-zinc-100">
            Required to send emails + track replies.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div data-testid="gmail-status-connected" className="text-sm text-zinc-100">
              Status: {gmailStatus?.connected ? "Connected" : "Not connected"}
            </div>
            <div data-testid="gmail-status-email" className="text-sm text-zinc-300">{gmailStatus?.email_address || ""}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="gmail-open-settings-button" variant="secondary" className="rounded-xl bg-white/20 text-zinc-100 hover:bg-white/[0.18]" asChild>
              <Link to="/settings">Open settings</Link>
            </Button>
            <Button data-testid="gmail-poll-replies-button" onClick={pollReplies} variant="secondary" className="rounded-xl bg-white/20 text-zinc-100 hover:bg-white/[0.18]">
              Poll replies
            </Button>
            <Button data-testid="generate-followups-button" onClick={generateFollowups} variant="secondary" className="rounded-xl bg-white/20 text-zinc-100 hover:bg-white/[0.18]">
              Generate follow-ups
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="generate" className="space-y-4" data-testid="outreach-tabs">
        <TabsList data-testid="outreach-tabs-list" className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border border-white/25">
          <TabsTrigger data-testid="outreach-tab-generate" value="generate">Generate</TabsTrigger>
          <TabsTrigger data-testid="outreach-tab-drafts" value="drafts">Drafts</TabsTrigger>
          <TabsTrigger data-testid="outreach-tab-sent" value="sent">Sent</TabsTrigger>
        </TabsList>

        <TabsContent value="generate" data-testid="outreach-generate-tab" className="space-y-4">
          <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="outreach-generate-card">
            <CardHeader>
              <CardTitle data-testid="outreach-generate-title">Generate drafts (AI)</CardTitle>
              <CardDescription data-testid="outreach-generate-desc" className="text-zinc-100">
                Select a pitch (offer), select contacts, add optional notes, then generate drafts.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <div data-testid="outreach-offer-label" className="text-sm text-zinc-100">Offer</div>
                  <select
                    data-testid="outreach-offer-select"
                    value={offerId}
                    onChange={(e) => setOfferId(e.target.value)}
                    className="w-full rounded-xl border border-white/25 bg-[#1a1b2e] px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="" className="bg-[#1a1b2e] text-zinc-100">Select offer…</option>
                    {(offers || []).map((o) => (
                      <option key={o.id} value={o.id} className="bg-[#1a1b2e] text-zinc-100">
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div data-testid="outreach-extra-notes-label" className="text-sm text-zinc-100">Extra notes (optional)</div>
                  <Input
                    data-testid="outreach-extra-notes-input"
                    value={extraNotes}
                    onChange={(e) => setExtraNotes(e.target.value)}
                    className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25"
                    placeholder="Example: mention their new Instagram campaign around product launch"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
                <div data-testid="outreach-contacts-title" className="text-sm font-medium text-zinc-100">Contacts</div>
                <div data-testid="outreach-contacts-hint" className="text-xs text-zinc-300">Tip: start with marketing / brand / partnerships roles.</div>

                <div className="mt-3 max-h-[320px] overflow-auto rounded-lg border border-white/25">
                  <Table data-testid="outreach-contacts-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-zinc-100">Select</TableHead>
                        <TableHead className="text-zinc-100">Contact</TableHead>
                        <TableHead className="text-zinc-100">Company</TableHead>
                        <TableHead className="text-zinc-100">Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(contactsEnriched || []).map((ct) => (
                        <TableRow 
                          key={ct.id} 
                          className={cn(
                            "border-white/25 transition-all",
                            deletingContacts && selected[ct.id] && "delete-removing"
                          )}
                        >
                          <TableCell>
                            <input
                              data-testid={`outreach-contact-checkbox-${ct.id}`}
                              type="checkbox"
                              checked={!!selected[ct.id]}
                              onChange={(e) => setSelected((p) => ({ ...p, [ct.id]: e.target.checked }))}
                              disabled={deletingContacts}
                            />
                          </TableCell>
                          <TableCell>
                            <div data-testid={`outreach-contact-name-${ct.id}`} className="text-sm text-zinc-100">
                              {(ct.first_name || "").trim()} {(ct.last_name || "").trim()}
                            </div>
                            <div data-testid={`outreach-contact-title-${ct.id}`} className="text-xs text-zinc-300">{ct.title || "—"}</div>
                          </TableCell>
                          <TableCell>
                            <div data-testid={`outreach-contact-company-${ct.id}`} className="text-sm text-zinc-100">{ct.company?.name || "—"}</div>
                          </TableCell>
                          <TableCell>
                            <div data-testid={`outreach-contact-email-${ct.id}`} className="text-sm text-zinc-100">{ct.email}</div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div data-testid="outreach-selected-count" className="text-sm text-zinc-100">Selected: {selectedIds.length}</div>
                    {selectedIds.length > 0 && (
                      <Button
                        data-testid="outreach-delete-contacts-button"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          deleteSelectedContacts();
                        }}
                        variant="secondary"
                        size="sm"
                        className="delete-btn bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 rounded-lg text-xs px-2 py-1"
                        disabled={deletingContacts}
                      >
                        {deletingContacts ? "Deleting..." : "Delete selected"}
                      </Button>
                    )}
                  </div>
                  <Button
                    data-testid="outreach-generate-button"
                    onClick={generateDrafts}
                    disabled={!offerId || selectedIds.length === 0 || busy}
                    className="rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300"
                  >
                    {busy ? "Generating…" : "Generate drafts"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="drafts" data-testid="outreach-drafts-tab" className="space-y-4">
          {dLoading ? <div data-testid="outreach-drafts-loading" className="text-sm text-zinc-100">Loading…</div> : null}
          {dError ? <div data-testid="outreach-drafts-error" className="text-sm text-rose-200">{dError}</div> : null}

          {(drafts || []).length === 0 ? (
            <div data-testid="outreach-drafts-empty" className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 text-sm text-zinc-100">
              No drafts yet. Generate some from the Generate tab.
            </div>
          ) : (
            <div className="grid gap-3">
              {(drafts || []).map((d) => (
                <DraftCard key={`${d.id}:${d.updated_at || ""}`} draft={d} contacts={contactsEnriched} companies={companies || []} onSave={saveDraft} onSend={sendDraft} onDelete={deleteDraft} gmailConnected={!!gmailStatus?.connected} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent" data-testid="outreach-sent-tab" className="space-y-4">
          <div className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
            <div data-testid="outreach-sent-hint" className="text-sm text-zinc-100">Tip: click “Poll replies” to update reply status.</div>
          </div>

          <div className="max-h-[520px] overflow-auto rounded-xl border border-white/25">
            <Table data-testid="outreach-sent-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-zinc-100">Status</TableHead>
                  <TableHead className="text-zinc-100">Company</TableHead>
                  <TableHead className="text-zinc-100">Contact</TableHead>
                  <TableHead className="text-zinc-100">Subject</TableHead>
                  <TableHead className="text-zinc-100">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sent || []).map((s) => {
                  const ct = (contactsEnriched || []).find((c) => c.id === s.contact_id);
                  const comp = (companies || []).find((c) => c.id === s.company_id);
                  return (
                    <TableRow 
                      key={s.id} 
                      className={cn(
                        "border-white/25 transition-all",
                        deletingSentId === s.id && "delete-removing"
                      )}
                    >
                      <TableCell>
                        <Badge
                          data-testid={`sent-status-${s.id}`}
                          className={cn(
                            "border",
                            s.status === "SENT" ? "bg-emerald-500/15 text-emerald-100 border-emerald-500/20" : s.status === "REPLIED" ? "bg-sky-500/15 text-sky-100 border-sky-500/20" : "bg-rose-500/15 text-rose-100 border-rose-500/20",
                          )}
                        >
                          {s.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div data-testid={`sent-company-${s.id}`} className="text-sm text-zinc-100">{comp?.name || "—"}</div>
                      </TableCell>
                      <TableCell>
                        <div data-testid={`sent-contact-${s.id}`} className="text-sm text-zinc-100">{(ct?.first_name || "").trim()} {(ct?.last_name || "").trim()}</div>
                        <div data-testid={`sent-email-${s.id}`} className="text-xs text-zinc-300">{ct?.email || ""}</div>
                      </TableCell>
                      <TableCell>
                        <div data-testid={`sent-subject-${s.id}`} className="text-sm text-zinc-100">{s.subject}</div>
                        {s.error_message ? <div data-testid={`sent-error-${s.id}`} className="mt-1 text-xs text-rose-200">{s.error_message}</div> : null}
                      </TableCell>
                      <TableCell>
                        <Button 
                          data-testid={`sent-delete-${s.id}`}
                          type="button"
                          variant="secondary" 
                          size="sm"
                          className="delete-btn bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 rounded-lg text-xs px-2 py-1"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteSent(s.id);
                          }}
                          disabled={deletingSentId === s.id}
                        >
                          {deletingSentId === s.id ? "..." : "Delete"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {(sent || []).length === 0 ? (
            <div data-testid="outreach-sent-empty" className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 text-sm text-zinc-100">Nothing sent yet.</div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const DraftCard = ({ draft, contacts, companies, onSave, onSend, onDelete, gmailConnected }) => {
  const ct = (contacts || []).find((c) => c.id === draft.contact_id);
  const comp = (companies || []).find((c) => c.id === draft.company_id);
  const [local, setLocal] = useState({ subject: draft.subject, body_text: draft.body_text });
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    await new Promise(r => setTimeout(r, 700));
    onDelete(draft.id);
  };

  return (
    <Card className={cn(
      "border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition-all",
      deleting && "delete-removing"
    )} data-testid={`draft-card-${draft.id}`}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle data-testid={`draft-company-${draft.id}`} className="text-base">{comp?.name || "—"}</CardTitle>
            <CardDescription data-testid={`draft-contact-${draft.id}`} className="text-zinc-100">
              To: {(ct?.first_name || "").trim()} {(ct?.last_name || "").trim()} • {ct?.email || ""}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge data-testid={`draft-step-${draft.id}`} variant="secondary" className="bg-white/20 text-zinc-100 border-white/25">
              Step {draft.step_index}
            </Badge>
            <Button 
              data-testid={`draft-delete-${draft.id}`}
              type="button"
              variant="secondary" 
              size="sm"
              className="delete-btn bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 rounded-lg text-xs px-2 py-1"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }}
              disabled={deleting}
            >
              {deleting ? "..." : "Delete"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div data-testid={`draft-subject-label-${draft.id}`} className="text-sm text-zinc-100">Subject</div>
          <Input data-testid={`draft-subject-input-${draft.id}`} value={local.subject} onChange={(e) => setLocal({ ...local, subject: e.target.value })} className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25" />
        </div>
        <div>
          <div data-testid={`draft-body-label-${draft.id}`} className="text-sm text-zinc-100">Body</div>
          <Textarea data-testid={`draft-body-input-${draft.id}`} value={local.body_text} onChange={(e) => setLocal({ ...local, body_text: e.target.value })} className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25 min-h-[160px]" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid={`draft-save-${draft.id}`} onClick={() => onSave(draft.id, { subject: local.subject, body_text: local.body_text })} className="rounded-xl bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-[#041014] shadow-[0_18px_55px_-38px_rgba(45,212,191,0.70)] hover:from-emerald-300 hover:via-teal-300 hover:to-cyan-300">
            Save draft
          </Button>
          <Button
            data-testid={`draft-send-${draft.id}`}
            onClick={() => onSend(draft.id)}
            disabled={!gmailConnected}
            className={cn("rounded-xl", gmailConnected ? "bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300" : "bg-white/20 text-zinc-300")}
          >
            Send
          </Button>
          {!gmailConnected ? (
            <div data-testid={`draft-send-disabled-hint-${draft.id}`} className="self-center text-xs text-zinc-300">
              Connect Gmail in Settings to enable sending.
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

// ----------------------
// Inbox Pages
// ----------------------

const InboxPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all, unread, starred
  const [deletingId, setDeletingId] = useState(null);

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === "unread") params.append("unread", "true");
      if (filter === "starred") params.append("starred", "true");
      if (search) params.append("search", search);
      const res = await api.get(`/inbox/threads?${params.toString()}`);
      setThreads(res.data);
    } catch (e) {
      toast({ title: "Failed to load inbox", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Auto-sync on component mount
  useEffect(() => {
    const autoSync = async () => {
      try {
        await api.post("/inbox/sync");
        await fetchThreads();
      } catch (e) {
        // Silent fail for auto-sync, just load existing threads
        await fetchThreads();
      }
    };
    autoSync();
  }, []);

  // Refetch when filter changes
  useEffect(() => {
    fetchThreads();
  }, [filter]);

  // Auto-sync every 60 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await api.post("/inbox/sync");
        await fetchThreads();
      } catch (e) {
        // Silent fail for background sync
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [filter, search]);

  const syncInbox = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/inbox/sync");
      toast({ title: "Synced", description: `${res.data.synced} thread(s) synced from Gmail.` });
      await fetchThreads();
    } catch (e) {
      toast({ title: "Sync failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  const toggleStar = async (e, threadId, isStarred) => {
    e.stopPropagation();
    try {
      await api.post(`/inbox/threads/${threadId}/${isStarred ? "unstar" : "star"}`);
      setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, is_starred: !isStarred } : t)));
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const toggleRead = async (e, threadId, isRead) => {
    e.stopPropagation();
    try {
      await api.post(`/inbox/threads/${threadId}/${isRead ? "unread" : "read"}`);
      setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, is_read: !isRead } : t)));
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const deleteThread = async (e, threadId) => {
    e.stopPropagation();
    setDeletingId(threadId);
    await new Promise((r) => setTimeout(r, 400));
    try {
      await api.delete(`/inbox/threads/${threadId}`);
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      toast({ title: "Deleted", description: "Thread removed." });
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchThreads();
  };

  const filteredThreads = threads;

  return (
    <div data-testid="inbox-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div data-testid="inbox-title" className="text-lg font-semibold">Inbox</div>
          <div data-testid="inbox-subtitle" className="text-sm text-zinc-100">View and reply to email conversations.</div>
        </div>
        <Button
          data-testid="inbox-sync-button"
          onClick={syncInbox}
          disabled={syncing}
          className={cn(
            "rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300 transition-all active:scale-95",
            syncing && "opacity-80 cursor-wait"
          )}
        >
          {syncing ? (
            <span className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Syncing...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Sync from Gmail
            </span>
          )}
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-300" />
                <Input
                  data-testid="inbox-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search conversations..."
                  className="pl-10 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25"
                />
              </div>
            </form>
            <div className="flex items-center gap-2">
              <Button
                data-testid="inbox-filter-all"
                variant={filter === "all" ? "default" : "secondary"}
                size="sm"
                onClick={() => setFilter("all")}
                className={cn("rounded-lg", filter === "all" ? "bg-indigo-500 text-white" : "bg-white/20 text-zinc-100")}
              >
                All
              </Button>
              <Button
                data-testid="inbox-filter-unread"
                variant={filter === "unread" ? "default" : "secondary"}
                size="sm"
                onClick={() => setFilter("unread")}
                className={cn("rounded-lg", filter === "unread" ? "bg-indigo-500 text-white" : "bg-white/20 text-zinc-100")}
              >
                Unread
              </Button>
              <Button
                data-testid="inbox-filter-starred"
                variant={filter === "starred" ? "default" : "secondary"}
                size="sm"
                onClick={() => setFilter("starred")}
                className={cn("rounded-lg", filter === "starred" ? "bg-amber-500 text-white" : "bg-white/20 text-zinc-100")}
              >
                <Star className="h-3 w-3 mr-1" />
                Starred
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Thread List */}
      {loading ? (
        <div data-testid="inbox-loading" className="text-sm text-zinc-100">Loading...</div>
      ) : filteredThreads.length === 0 ? (
        <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
          <CardContent className="p-6 text-center">
            <Inbox className="h-12 w-12 mx-auto text-zinc-300 mb-3" />
            <div className="text-zinc-100">No conversations yet.</div>
            <div className="text-sm text-zinc-300 mt-1">Click "Sync from Gmail" to load conversations from sent emails.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredThreads.map((thread) => (
            <div
              key={thread.id}
              data-testid={`inbox-thread-${thread.id}`}
              onClick={() => navigate(`/inbox/${thread.id}`)}
              className={cn(
                "rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 cursor-pointer transition-all hover:bg-white/[0.12]",
                !thread.is_read && "border-l-4 border-l-indigo-500 unread-shine",
                deletingId === thread.id && "delete-removing"
              )}
            >
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center gap-2">
                  <button
                    data-testid={`inbox-star-${thread.id}`}
                    onClick={(e) => toggleStar(e, thread.id, thread.is_starred)}
                    className="text-zinc-300 hover:text-amber-400 transition-colors"
                  >
                    {thread.is_starred ? <Star className="h-5 w-5 fill-amber-400 text-amber-400" /> : <StarOff className="h-5 w-5" />}
                  </button>
                  <button
                    data-testid={`inbox-read-${thread.id}`}
                    onClick={(e) => toggleRead(e, thread.id, thread.is_read)}
                    className="text-zinc-300 hover:text-indigo-400 transition-colors"
                  >
                    {thread.is_read ? <MailOpen className="h-5 w-5" /> : <MailCheck className="h-5 w-5 text-indigo-400" />}
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className={cn("font-medium truncate", !thread.is_read && "text-white font-semibold")}>
                        {thread.company_name || thread.contact_name || "Unknown"}
                      </div>
                      {!thread.is_read && (
                        <span className="new-badge inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-500/30 text-indigo-200 border border-indigo-400/50">
                          New
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-300 whitespace-nowrap">
                      {thread.last_message_at ? new Date(thread.last_message_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <div className="text-sm text-zinc-100 truncate">{thread.contact_email}</div>
                  <div className={cn("text-sm mt-1 truncate", !thread.is_read ? "text-zinc-100" : "text-zinc-300")}>
                    {thread.subject}
                  </div>
                  <div className="text-xs text-zinc-300 mt-1 truncate">{thread.snippet}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary" className="bg-white/20 text-zinc-100 border-white/25 text-xs">
                      {thread.message_count} message{thread.message_count !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                </div>
                <button
                  data-testid={`inbox-delete-${thread.id}`}
                  onClick={(e) => deleteThread(e, thread.id)}
                  className="delete-btn text-zinc-300 hover:text-rose-400 transition-colors p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InboxThreadPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [expandedQuotes, setExpandedQuotes] = useState({});
  const [attachmentModal, setAttachmentModal] = useState(null); // { attachment, blobUrl, loading }
  const messagesEndRef = useRef(null);

  // Parse message body to separate main content from quoted text
  const parseMessageBody = (bodyText) => {
    if (!bodyText) return { mainContent: "", quotedContent: "", hasQuote: false };
    
    const lines = bodyText.split('\n');
    const mainLines = [];
    const quotedLines = [];
    let inQuote = false;
    
    for (const line of lines) {
      // Detect quoted lines (starting with > or "On ... wrote:")
      if (line.trim().startsWith('>') || line.match(/^On .+ wrote:$/i)) {
        inQuote = true;
      }
      
      if (inQuote) {
        quotedLines.push(line);
      } else {
        mainLines.push(line);
      }
    }
    
    return {
      mainContent: mainLines.join('\n').trim(),
      quotedContent: quotedLines.join('\n').trim(),
      hasQuote: quotedLines.length > 0
    };
  };

  // Get preview of quoted message (first 50 chars)
  const getQuotePreview = (quotedContent) => {
    const cleanedQuote = quotedContent.replace(/^>/gm, '').trim();
    return cleanedQuote.substring(0, 60) + (cleanedQuote.length > 60 ? '...' : '');
  };

  const toggleQuoteExpanded = (msgId) => {
    setExpandedQuotes(prev => ({ ...prev, [msgId]: !prev[msgId] }));
  };

  const handleReplyToMessage = (msg) => {
    setReplyingTo(msg);
    // Scroll to reply box
    document.querySelector('[data-testid="inbox-reply-input"]')?.focus();
  };

  const clearReplyingTo = () => {
    setReplyingTo(null);
  };

  // Detect Safari browser
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Download helper - use different methods for Safari vs Chrome
  const triggerDownload = (url, filename) => {
    if (isSafari) {
      // Safari: Direct navigation triggers download with FileResponse
      window.location.href = url;
    } else {
      // Chrome/Firefox: Anchor click with download attribute
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Direct download attachment
  const downloadAttachmentDirect = (e, attachment) => {
    e.preventDefault();
    e.stopPropagation();
    
    const filename = attachment.filename || 'attachment';
    const downloadUrl = `${API}/inbox/attachment/${attachment.message_id}/${attachment.attachment_id}?filename=${encodeURIComponent(filename)}`;
    
    toast({ title: "Downloading...", description: filename });
    triggerDownload(downloadUrl, filename);
  };

  // Open attachment in preview modal
  const openAttachment = async (e, attachment) => {
    e.preventDefault();
    e.stopPropagation();
    
    const filename = attachment.filename || 'attachment';
    const mimeType = attachment.mime_type || 'application/octet-stream';
    const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For Safari PDFs, use direct URL instead of blob (Safari has issues with blob URLs for PDFs)
    if (isSafariBrowser && mimeType === 'application/pdf') {
      const directUrl = `${API}/inbox/attachment/${attachment.message_id}/${attachment.attachment_id}?filename=${encodeURIComponent(filename)}&preview=true`;
      setAttachmentModal({ 
        attachment, 
        blobUrl: directUrl, 
        loading: false, 
        textContent: null,
        isDirectUrl: true 
      });
      return;
    }
    
    setAttachmentModal({ attachment, blobUrl: null, loading: true, textContent: null });
    
    try {
      const response = await api.get(
        `/inbox/attachment/${attachment.message_id}/${attachment.attachment_id}?filename=${encodeURIComponent(filename)}&preview=true`,
        { responseType: 'blob' }
      );
      
      // Use the mime_type from attachment metadata, or detect from response
      const blob = new Blob([response.data], { type: mimeType });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // For CSV/text files, read the text content for preview
      let textContent = null;
      const isTextFile = mimeType?.includes('csv') || 
          mimeType?.startsWith('text/') ||
          filename?.toLowerCase().endsWith('.csv') ||
          filename?.toLowerCase().endsWith('.txt');
          
      if (isTextFile) {
        try {
          textContent = await blob.text();
          console.log("CSV Content loaded, length:", textContent?.length);
        } catch (e) {
          console.error("Could not read text content:", e);
        }
      }
      
      setAttachmentModal({ attachment, blobUrl, blob, loading: false, textContent });
    } catch (err) {
      console.error("Failed to load attachment:", err);
      toast({ title: "Failed to load", description: err?.response?.data?.detail || err.message, variant: "destructive" });
      setAttachmentModal(null);
    }
  };

  // Close attachment modal
  const closeAttachmentModal = () => {
    if (attachmentModal?.blobUrl) {
      window.URL.revokeObjectURL(attachmentModal.blobUrl);
    }
    setAttachmentModal(null);
  };

  // Download from modal
  const downloadFromModal = () => {
    if (!attachmentModal?.attachment) return;
    
    const { attachment } = attachmentModal;
    const filename = attachment.filename || "attachment";
    const downloadUrl = `${API}/inbox/attachment/${attachment.message_id}/${attachment.attachment_id}?filename=${encodeURIComponent(filename)}`;
    
    toast({ title: "Downloading...", description: filename });
    triggerDownload(downloadUrl, filename);
  };

  // Check if file is previewable
  const isPreviewable = (mimeType) => {
    if (!mimeType) return false;
    return mimeType.startsWith('image/') || 
           mimeType === 'application/pdf' ||
           mimeType.startsWith('text/') ||
           mimeType === 'text/csv';
  };

  // Parse CSV for preview - improved parsing
  const parseCSVPreview = (text, maxRows = 10) => {
    if (!text) return [];
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) return [];
    
    const rows = lines.slice(0, maxRows).map(line => {
      // Handle CSV with commas inside quotes
      const cells = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          cells.push(current.trim().replace(/^"|"$/g, ''));
          current = '';
        } else {
          current += char;
        }
      }
      cells.push(current.trim().replace(/^"|"$/g, ''));
      return cells;
    });
    
    // Ensure all rows have the same number of columns as header
    if (rows.length > 1) {
      const headerLength = rows[0].length;
      for (let i = 1; i < rows.length; i++) {
        while (rows[i].length < headerLength) {
          rows[i].push('');
        }
      }
    }
    
    return rows;
  };

  const fetchThread = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inbox/threads/${threadId}`);
      setThread(res.data);
      setReply(res.data.draft?.body_text || "");
      // Mark as read
      if (!res.data.is_read) {
        await api.post(`/inbox/threads/${threadId}/read`);
      }
    } catch (e) {
      toast({ title: "Failed to load thread", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThread();
  }, [threadId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [thread?.messages]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/inbox/threads/${threadId}/reply`, { body_text: reply });
      toast({ title: "Sent", description: "Your reply has been sent." });
      setReply("");
      await fetchThread();
    } catch (e) {
      toast({ title: "Send failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const saveDraft = async () => {
    if (!reply.trim()) return;
    setSavingDraft(true);
    try {
      await api.put(`/inbox/threads/${threadId}/draft`, { body_text: reply });
      toast({ title: "Draft saved", description: "Your draft has been saved." });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSavingDraft(false);
    }
  };

  const toggleStar = async () => {
    if (!thread) return;
    try {
      await api.post(`/inbox/threads/${threadId}/${thread.is_starred ? "unstar" : "star"}`);
      setThread((prev) => prev ? { ...prev, is_starred: !prev.is_starred } : prev);
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  if (loading) {
    return <div className="text-sm text-zinc-100">Loading...</div>;
  }

  if (!thread) {
    return <div className="text-sm text-zinc-100">Thread not found.</div>;
  }

  return (
    <div data-testid="inbox-thread-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          data-testid="inbox-back-button"
          variant="secondary"
          size="sm"
          onClick={() => navigate("/inbox")}
          className="rounded-lg bg-white/20 text-zinc-100 hover:bg-white/30"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          <div className="font-semibold text-lg">{thread.subject}</div>
          <div className="text-sm text-zinc-100">
            {thread.company?.name} • {thread.contact?.first_name} {thread.contact?.last_name} ({thread.contact?.email})
          </div>
        </div>
        <button onClick={toggleStar} className="text-zinc-100 hover:text-amber-400 transition-colors">
          {thread.is_starred ? <Star className="h-6 w-6 fill-amber-400 text-amber-400" /> : <StarOff className="h-6 w-6" />}
        </button>
      </div>

      {/* Messages */}
      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
        <CardContent className="p-0">
          <div className="max-h-[400px] overflow-y-auto p-4 space-y-4">
            {(thread.messages || []).map((msg, idx) => {
              const { mainContent, quotedContent, hasQuote } = parseMessageBody(msg.body_text);
              const isExpanded = expandedQuotes[msg.id];
              
              return (
                <div
                  key={msg.id}
                  data-testid={`inbox-message-${msg.id}`}
                  className={cn(
                    "rounded-lg p-4",
                    msg.is_outgoing
                      ? "bg-gradient-to-r from-emerald-600/30 via-emerald-500/20 to-teal-600/25 border border-emerald-500/40 ml-8 shadow-[0_4px_20px_-5px_rgba(16,185,129,0.3)]"
                      : "bg-gradient-to-r from-amber-600/30 via-amber-500/20 to-orange-600/25 border border-amber-500/40 mr-8 shadow-[0_4px_20px_-5px_rgba(245,158,11,0.3)]"
                  )}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={cn(
                      "text-base font-bold",
                      msg.is_outgoing ? "text-emerald-100" : "text-amber-100"
                    )}>
                      {msg.is_outgoing ? "You" : msg.from_name || msg.from_email}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-zinc-200 font-medium">
                        {msg.sent_at ? new Date(msg.sent_at).toLocaleString() : ""}
                      </div>
                      {!msg.is_outgoing && (
                        <button
                          onClick={() => handleReplyToMessage(msg)}
                          className="text-xs px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 text-zinc-100 transition-colors flex items-center gap-1"
                        >
                          <Reply className="h-3 w-3" />
                          Reply
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Main message content */}
                  <div className="text-[15px] leading-relaxed whitespace-pre-wrap text-white font-medium">
                    {mainContent || msg.body_text}
                  </div>
                  
                  {/* Attachments - clean display */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.attachments.map((att, attIdx) => (
                        <div key={attIdx} className="inline-flex items-center gap-1">
                          <button
                            onClick={(e) => openAttachment(e, att)}
                            className={cn(
                              "inline-flex items-center gap-2 px-3 py-1.5 rounded-l-lg text-xs font-medium transition-all hover:scale-105",
                              "bg-white/10 hover:bg-white/20 border-y border-l cursor-pointer",
                              msg.is_outgoing ? "border-emerald-400/30 text-emerald-100" : "border-amber-400/30 text-amber-100"
                            )}
                            title="Preview"
                          >
                            <Paperclip className="h-3 w-3" />
                            <span className="max-w-[120px] truncate">{att.filename}</span>
                            {att.size > 0 && (
                              <span className="text-zinc-400 text-[10px]">
                                ({(att.size / 1024).toFixed(0)}KB)
                              </span>
                            )}
                          </button>
                          <button
                            onClick={(e) => downloadAttachmentDirect(e, att)}
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-1.5 rounded-r-lg text-xs font-medium transition-all hover:scale-105",
                              "bg-white/20 hover:bg-white/30 border cursor-pointer",
                              msg.is_outgoing ? "border-emerald-400/30 text-emerald-100" : "border-amber-400/30 text-amber-100"
                            )}
                            title="Download"
                          >
                            <Download className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Quoted/Referenced message - collapsible */}
                  {hasQuote && (
                    <div className="mt-3">
                      <button
                        onClick={() => toggleQuoteExpanded(msg.id)}
                        className={cn(
                          "w-full text-left rounded-md p-2 transition-all",
                          "bg-black/20 border-l-2",
                          msg.is_outgoing ? "border-emerald-400/50" : "border-amber-400/50"
                        )}
                      >
                        <div className="flex items-center gap-2 text-xs text-zinc-300">
                          <MessageSquare className="h-3 w-3" />
                          <span className="font-medium">Replying to:</span>
                          {!isExpanded && (
                            <span className="text-zinc-400 truncate flex-1">{getQuotePreview(quotedContent)}</span>
                          )}
                          <ChevronDown className={cn(
                            "h-3 w-3 transition-transform",
                            isExpanded && "rotate-180"
                          )} />
                        </div>
                        {isExpanded && (
                          <div className="mt-2 text-xs text-zinc-400 whitespace-pre-wrap max-h-32 overflow-y-auto">
                            {quotedContent.replace(/^>/gm, '').trim()}
                          </div>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        </CardContent>
      </Card>

      {/* Reply Box */}
      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Reply</CardTitle>
            {replyingTo && (
              <div className="flex items-center gap-2">
                <div className="text-xs text-zinc-300 bg-white/10 rounded-md px-2 py-1 flex items-center gap-1">
                  <Reply className="h-3 w-3" />
                  Replying to {replyingTo.from_name || replyingTo.from_email}
                </div>
                <button
                  onClick={clearReplyingTo}
                  className="text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Show reference to message being replied to */}
          {replyingTo && (
            <div className="bg-black/20 border-l-2 border-indigo-400/50 rounded-md p-2 text-xs text-zinc-400">
              <div className="font-medium text-zinc-300 mb-1">{replyingTo.from_name || replyingTo.from_email} wrote:</div>
              <div className="truncate">{parseMessageBody(replyingTo.body_text).mainContent.substring(0, 100)}...</div>
            </div>
          )}
          <Textarea
            data-testid="inbox-reply-input"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write your reply..."
            className="min-h-[120px] bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="inbox-send-reply"
              onClick={sendReply}
              disabled={sending || !reply.trim()}
              className={cn(
                "rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300 transition-all active:scale-95",
                sending && "opacity-80 cursor-wait"
              )}
            >
              {sending ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Send Reply
                </span>
              )}
            </Button>
            <Button
              data-testid="inbox-save-draft"
              onClick={saveDraft}
              disabled={savingDraft || !reply.trim()}
              variant="secondary"
              className="rounded-xl bg-white/20 text-zinc-100 hover:bg-white/30"
            >
              {savingDraft ? "Saving..." : "Save Draft"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Attachment Preview Modal */}
      {attachmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={closeAttachmentModal}>
          <div 
            className="relative bg-zinc-900 border border-white/20 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Paperclip className="h-5 w-5 text-indigo-400" />
                <div>
                  <h3 className="font-semibold text-white">{attachmentModal.attachment?.filename || 'Attachment'}</h3>
                  <p className="text-xs text-zinc-400">
                    {attachmentModal.attachment?.mime_type} • {((attachmentModal.attachment?.size || 0) / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button 
                onClick={closeAttachmentModal}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>

            {/* Modal Content - Preview */}
            <div className="p-4 max-h-[50vh] overflow-auto bg-zinc-950/50">
              {attachmentModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw className="h-8 w-8 animate-spin text-indigo-400" />
                  <span className="ml-3 text-zinc-300">Loading preview...</span>
                </div>
              ) : attachmentModal.blobUrl ? (
                <div className="flex items-center justify-center">
                  {attachmentModal.attachment?.mime_type?.startsWith('image/') ? (
                    <img 
                      src={attachmentModal.blobUrl} 
                      alt={attachmentModal.attachment.filename}
                      className="max-w-full max-h-[45vh] object-contain rounded-lg"
                    />
                  ) : attachmentModal.attachment?.mime_type === 'application/pdf' ? (
                    <iframe 
                      src={attachmentModal.blobUrl}
                      className="w-full h-[45vh] rounded-lg bg-white"
                      title={attachmentModal.attachment?.filename}
                    />
                  ) : attachmentModal.textContent ? (
                    <div className="w-full">
                      {(attachmentModal.attachment?.mime_type?.includes('csv') || attachmentModal.attachment?.filename?.endsWith('.csv')) ? (
                        <div className="overflow-x-auto max-h-[50vh]">
                          <table className="w-full text-xs border-collapse">
                            <tbody>
                              {parseCSVPreview(attachmentModal.textContent, 15).map((row, rowIdx) => (
                                <tr key={rowIdx} className={rowIdx === 0 ? "bg-indigo-500/20 sticky top-0" : "hover:bg-white/5"}>
                                  {row.slice(0, 10).map((cell, cellIdx) => (
                                    <td 
                                      key={cellIdx} 
                                      className={cn(
                                        "border border-white/10 px-2 py-1.5 text-zinc-300 max-w-[150px] truncate",
                                        rowIdx === 0 && "font-semibold text-white bg-indigo-500/30"
                                      )}
                                      title={cell}
                                    >
                                      {cell || '-'}
                                    </td>
                                  ))}
                                  {row.length > 10 && rowIdx === 0 && (
                                    <td className="border border-white/10 px-2 py-1.5 text-zinc-500 font-semibold">
                                      +{row.length - 10} more
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="text-xs text-zinc-500 mt-2 text-center">
                            Showing first 15 rows, {Math.min(10, parseCSVPreview(attachmentModal.textContent, 1)[0]?.length || 0)} columns
                          </p>
                        </div>
                      ) : (
                        <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono bg-black/30 p-4 rounded-lg max-h-[40vh] overflow-auto">
                          {attachmentModal.textContent.substring(0, 5000)}
                          {attachmentModal.textContent.length > 5000 && "\n\n... (truncated)"}
                        </pre>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="h-16 w-16 mx-auto text-zinc-500 mb-4" />
                      <p className="text-zinc-300 font-medium">{attachmentModal.attachment?.filename}</p>
                      <p className="text-zinc-500 text-sm mt-1">Preview not available for this file type</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-zinc-500">Failed to load preview</p>
                </div>
              )}
            </div>

            {/* Modal Footer - Download Button */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10 bg-zinc-900">
              <Button
                onClick={closeAttachmentModal}
                variant="secondary"
                className="rounded-xl bg-white/10 text-zinc-300 hover:bg-white/20"
              >
                Close
              </Button>
              <Button
                onClick={downloadFromModal}
                disabled={!attachmentModal?.attachment}
                className="rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-400 hover:to-purple-400 flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download File
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReportsPage = () => {
  const { toast } = useToast();
  const dateRef = useRef(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { data, loading, error, setData } = useApiData(
    () => api.get(`/reports/daily?date=${encodeURIComponent(date)}`).then((r) => r.data),
    [date],
  );

  const refresh = async () => {
    setRefreshing(true);
    try {
      // First sync inbox to detect and update replies
      await api.post("/inbox/sync");
      // Then fetch updated data
      const d = await api.get(`/reports/daily?date=${encodeURIComponent(date)}`).then((r) => r.data);
      setData(d);
      toast({ title: "Updated", description: "Daily sheet refreshed with latest reply status." });
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  const exportCSV = () => {
    setExporting(true);
    const filename = `outreach_${date}.csv`;
    toast({ title: "Exporting...", description: filename });
    
    // Detect Safari
    const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    if (isSafariBrowser) {
      // Safari: Direct navigation triggers download
      window.location.href = `${API}/export/outreach.csv`;
    } else {
      // Chrome/Firefox: Anchor click with download attribute
      const link = document.createElement('a');
      link.href = `${API}/export/outreach.csv`;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    setTimeout(() => setExporting(false), 1000);
  };

  return (
    <div data-testid="reports-page" className="space-y-4">
      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="reports-filter-card">
        <CardHeader>
          <CardTitle data-testid="reports-title">Daily Outreach Sheet</CardTitle>
          <CardDescription data-testid="reports-desc" className="text-zinc-100">
            Columns always show. Rows appear after you <span className="text-zinc-100">Send</span> emails (status SENT/REPLIED) for the selected date.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <div data-testid="reports-date-label" className="text-sm text-zinc-100">Date</div>
            <Input
              data-testid="reports-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25"
            />
          </div>
          <Button 
            data-testid="reports-refresh" 
            onClick={refresh} 
            disabled={refreshing}
            className={cn(
              "rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300 transition-all",
              refreshing && "opacity-80 cursor-wait"
            )}
          >
            {refreshing ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Refreshing...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </span>
            )}
          </Button>
          <Button 
            data-testid="reports-export-csv" 
            onClick={exportCSV} 
            disabled={exporting}
            variant="secondary" 
            className="rounded-xl bg-white/20 text-zinc-100 hover:bg-white/[0.18]"
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </CardContent>
      </Card>

      {loading ? <div data-testid="reports-loading" className="text-sm text-zinc-100">Loading…</div> : null}
      {error ? <div data-testid="reports-error" className="text-sm text-rose-200">{error}</div> : null}

      <div className="overflow-auto rounded-xl border border-white/25">
        <Table data-testid="reports-table" className="min-w-[1100px]">
          <TableHeader>
            <TableRow>
              <TableHead className="text-zinc-100 whitespace-nowrap">S.No</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Date</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Time</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Company</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Person</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Email</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Contact&nbsp;No</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Sent</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Reply</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Follow-up&nbsp;due</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Follow-up&nbsp;sent</TableHead>
              <TableHead className="text-zinc-100 whitespace-nowrap">Follow-up&nbsp;reply</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data || []).map((row) => (
              <TableRow key={row.s_no} className="border-white/25">
                <TableCell data-testid={`reports-sno-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.s_no}</TableCell>
                <TableCell data-testid={`reports-date-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.date}</TableCell>
                <TableCell data-testid={`reports-time-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.time}</TableCell>
                <TableCell data-testid={`reports-company-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.company_name}</TableCell>
                <TableCell data-testid={`reports-person-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.person_name}</TableCell>
                <TableCell data-testid={`reports-email-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.email}</TableCell>
                <TableCell data-testid={`reports-phone-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.contact_no}</TableCell>
                <TableCell data-testid={`reports-sent-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.sent}</TableCell>
                <TableCell data-testid={`reports-replied-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.replied}</TableCell>
                <TableCell data-testid={`reports-followup-due-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.followup_due}</TableCell>
                <TableCell data-testid={`reports-followup-sent-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.followup_sent}</TableCell>
                <TableCell data-testid={`reports-followup-replied-${row.s_no}`} className="text-zinc-100 whitespace-nowrap">{row.followup_replied}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {!loading && (data || []).length === 0 ? (
        <div data-testid="reports-empty" className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 text-sm text-zinc-100">
          No sent emails found for this date.
        </div>
      ) : null}
    </div>
  );
};

const AdminPage = ({ auth }) => {
  const { toast } = useToast();
  const { data, loading, error, setData } = useApiData(() => api.get("/admin/allowed-users").then((r) => r.data), []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "member" });

  if (auth.user?.role !== "admin") {
    return (
      <div data-testid="admin-not-allowed" className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 text-sm text-zinc-100">
        Admin access required.
      </div>
    );
  }

  const refresh = async () => {
    const d = await api.get("/admin/allowed-users").then((r) => r.data);
    setData(d);
  };

  const add = async () => {
    try {
      await api.post("/admin/allowed-users", form);
      toast({ title: "Added", description: "User added to allow-list." });
      setOpen(false);
      setForm({ email: "", role: "member" });
      await refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const toggleActive = async (email, active) => {
    try {
      await api.put(`/admin/allowed-users/${encodeURIComponent(email)}`, { active });
      toast({ title: "Updated", description: active ? "Access enabled" : "Access revoked" });
      await refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const changeRole = async (email, role) => {
    try {
      await api.put(`/admin/allowed-users/${encodeURIComponent(email)}`, { role });
      toast({ title: "Updated", description: "Role changed." });
      await refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const remove = async (email) => {
    try {
      await api.delete(`/admin/allowed-users/${encodeURIComponent(email)}`);
      toast({ title: "Removed", description: "User removed from allow-list." });
      await refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  return (
    <div data-testid="admin-page" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div data-testid="admin-title" className="text-lg font-semibold">Team access (allow-list)</div>
          <div data-testid="admin-subtitle" className="text-sm text-zinc-100">
            Add/remove users. Turning off access instantly logs them out.
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-add-user-button" className="rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300">Add user</Button>
          </DialogTrigger>
          <DialogContent data-testid="admin-add-user-dialog" className="border-white/25 bg-[#0b0c18] text-zinc-100">
            <DialogHeader>
              <DialogTitle data-testid="admin-add-user-title">Add allowed user</DialogTitle>
              <DialogDescription data-testid="admin-add-user-desc" className="text-zinc-100">
                Only allow-listed users can login.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input data-testid="admin-add-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@domain.com" className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25" />
              <div>
                <div data-testid="admin-add-user-role-label" className="text-sm text-zinc-100">Role</div>
                <select
                  data-testid="admin-add-user-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full rounded-xl border border-white/25 bg-[#1a1b2e] px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="member" className="bg-[#1a1b2e] text-zinc-100">Member</option>
                  <option value="admin" className="bg-[#1a1b2e] text-zinc-100">Admin</option>
                </select>
              </div>
              <Button data-testid="admin-add-user-submit" onClick={add} disabled={!form.email.trim()} className="rounded-xl bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-[#041014] shadow-[0_18px_55px_-38px_rgba(45,212,191,0.70)] hover:from-emerald-300 hover:via-teal-300 hover:to-cyan-300">
                Add
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div data-testid="admin-loading" className="text-sm text-zinc-100">Loading…</div> : null}
      {error ? <div data-testid="admin-error" className="text-sm text-rose-200">{error}</div> : null}

      <div className="overflow-auto rounded-xl border border-white/25">
        <Table data-testid="admin-users-table">
          <TableHeader>
            <TableRow>
              <TableHead className="text-zinc-100">Email</TableHead>
              <TableHead className="text-zinc-100">Role</TableHead>
              <TableHead className="text-zinc-100">Active</TableHead>
              <TableHead className="text-zinc-100">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data || []).map((u) => (
              <TableRow key={u.email} className="border-white/25">
                <TableCell data-testid={`admin-user-email-${u.email}`} className="text-zinc-100">{u.email}</TableCell>
                <TableCell>
                  <select
                    data-testid={`admin-user-role-${u.email}`}
                    value={u.role}
                    onChange={(e) => changeRole(u.email, e.target.value)}
                    className="rounded-lg border border-white/25 bg-[#1a1b2e] px-2 py-1 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="member" className="bg-[#1a1b2e] text-zinc-100">Member</option>
                    <option value="admin" className="bg-[#1a1b2e] text-zinc-100">Admin</option>
                  </select>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid={`admin-user-active-${u.email}`}
                      checked={!!u.active}
                      onCheckedChange={(checked) => toggleActive(u.email, checked)}
                    />
                    <span data-testid={`admin-user-active-label-${u.email}`} className="text-sm text-zinc-100">
                      {u.active ? "Yes" : "No"}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    data-testid={`admin-user-remove-${u.email}`}
                    variant="secondary"
                    className="h-9 rounded-xl bg-white/20 text-zinc-100 hover:bg-white/[0.18]"
                    onClick={() => remove(u.email)}
                  >
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {(data || []).length === 0 && !loading ? (
        <div data-testid="admin-empty" className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4 text-sm text-zinc-100">
          No allowed users.
        </div>
      ) : null}
    </div>
  );
};

const SettingsPage = () => {
  const { toast } = useToast();
  const { data: settings, loading: sLoading, error: sError, setData: setSettings } = useApiData(() => api.get("/settings").then((r) => r.data), []);
  const { data: gmailStatus, setData: setGmailStatus } = useApiData(() => api.get("/integrations/gmail/status").then((r) => r.data), []);
  const { data: decks, setData: setDecks } = useApiData(() => api.get("/decks").then((r) => r.data), []);

  const daysRef = useRef(null);
  const [deckFile, setDeckFile] = useState(null);
  const [deletingDeckId, setDeletingDeckId] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingDeck, setUploadingDeck] = useState(false);

  const saveSettings = async () => {
    const raw = daysRef.current?.value || "";
    const days = raw
      .split(",")
      .map((x) => parseInt(x.trim(), 10))
      .filter((x) => Number.isFinite(x) && x > 0)
      .slice(0, 10);

    setSavingSettings(true);
    try {
      const res = await api.put("/settings", { followup_days: days });
      setSettings(res.data);
      toast({ title: "Saved", description: "Follow-up sequence updated." });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadDeck = async () => {
    if (!deckFile || uploadingDeck) return;
    setUploadingDeck(true);
    try {
      const fd = new FormData();
      fd.append("file", deckFile);
      await api.post("/decks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "Uploaded", description: "Deck uploaded. It will attach to sends." });
      const newDecks = await api.get("/decks").then((r) => r.data);
      setDecks(newDecks);
      setDeckFile(null);
    } catch (e) {
      toast({ title: "Upload failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setUploadingDeck(false);
    }
  };

  const connectGmail = async () => {
    try {
      const redirectUri = `${BACKEND_URL}/api/integrations/gmail/callback`;
      const res = await api.post("/integrations/gmail/auth-url", { redirect_uri: redirectUri });
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast({ title: "Cannot start Gmail connect", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const disconnectGmail = async () => {
    try {
      await api.post("/integrations/gmail/disconnect");
      toast({ title: "Disconnected", description: "Gmail integration removed." });
      const st = await api.get("/integrations/gmail/status").then((r) => r.data);
      setGmailStatus(st);
    } catch (e) {
      toast({ title: "Disconnect failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  };

  const deleteDeck = async (deckId) => {
    setDeletingDeckId(deckId);
    await new Promise(r => setTimeout(r, 300));
    try {
      await api.delete(`/decks/${deckId}`);
      await new Promise(r => setTimeout(r, 400));
      setDecks((prev) => (prev || []).filter((d) => d.id !== deckId));
      toast({ title: "Deleted", description: "Deck removed." });
    } catch (e) {
      console.error("Delete deck error:", e);
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setDeletingDeckId(null);
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="settings-followup-card">
        <CardHeader>
          <CardTitle data-testid="settings-followup-title">Follow-up sequence</CardTitle>
          <CardDescription data-testid="settings-followup-desc" className="text-zinc-100">
            Configure days after the last sent email. Example: <span className="text-zinc-100">2,5,9</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            data-testid="settings-followup-input"
            ref={daysRef}
            defaultValue={(settings?.followup_days?.length ? settings.followup_days.join(",") : "2,5")}
            className="bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] border-white/25"
          />
          <Button 
            data-testid="settings-followup-save" 
            onClick={saveSettings} 
            disabled={savingSettings}
            className={cn(
              "rounded-xl bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-[#041014] shadow-[0_18px_55px_-38px_rgba(45,212,191,0.70)] hover:from-emerald-300 hover:via-teal-300 hover:to-cyan-300 transition-all active:scale-95",
              savingSettings && "opacity-80 cursor-wait"
            )}
          >
            {savingSettings ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : "Save sequence"}
          </Button>
          {sLoading ? <div data-testid="settings-followup-loading" className="text-sm text-zinc-100">Loading…</div> : null}
          {sError ? <div data-testid="settings-followup-error" className="text-sm text-rose-200">{sError}</div> : null}
        </CardContent>
      </Card>

      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="settings-deck-card">
        <CardHeader>
          <CardTitle data-testid="settings-deck-title">Deck PDF</CardTitle>
          <CardDescription data-testid="settings-deck-desc" className="text-zinc-100">
            Upload your pitch deck once. The latest uploaded deck will be attached to outgoing emails.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input data-testid="settings-deck-input" type="file" accept="application/pdf" onChange={(e) => setDeckFile(e.target.files?.[0] || null)} disabled={uploadingDeck} />
          <div className="flex flex-wrap items-center gap-2">
            <Button 
              data-testid="settings-deck-upload" 
              onClick={uploadDeck} 
              disabled={!deckFile || uploadingDeck} 
              className={cn(
                "rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300 transition-all active:scale-95",
                uploadingDeck && "opacity-80 cursor-wait"
              )}
            >
              {uploadingDeck ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </span>
              ) : "Upload deck"}
            </Button>
            {deckFile && !uploadingDeck ? (
              <Badge data-testid="settings-deck-filename" variant="secondary" className="bg-white/20 text-zinc-100 border-white/25">
                {deckFile.name}
              </Badge>
            ) : null}
          </div>

          <div className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-3">
            <div data-testid="settings-deck-list-title" className="text-sm font-medium text-zinc-100">Uploaded decks</div>
            <div className="mt-2 space-y-2">
              {(decks || []).map((d) => (
                <div 
                  key={d.id} 
                  className={cn(
                    "flex items-center justify-between gap-3 rounded-lg border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-2 transition-all",
                    deletingDeckId === d.id && "delete-removing"
                  )}
                >
                  <div>
                    <div data-testid={`deck-name-${d.id}`} className="text-sm text-zinc-100">{d.filename}</div>
                    <div data-testid={`deck-time-${d.id}`} className="text-xs text-zinc-300">Uploaded: {new Date(d.uploaded_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      data-testid={`deck-download-${d.id}`}
                      href={`${API}/decks/${d.id}/download`}
                      className="text-sm text-indigo-200 hover:text-indigo-100"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download
                    </a>
                    <Button 
                      data-testid={`deck-delete-${d.id}`}
                      type="button"
                      variant="secondary" 
                      size="sm"
                      className="delete-btn bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 rounded-lg text-xs px-2 py-1"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        deleteDeck(d.id);
                      }}
                      disabled={deletingDeckId === d.id}
                    >
                      {deletingDeckId === d.id ? "..." : "Delete"}
                    </Button>
                  </div>
                </div>
              ))}
              {(decks || []).length === 0 ? <div data-testid="settings-deck-empty" className="text-sm text-zinc-100">No deck uploaded yet.</div> : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="settings-gmail-card">
        <CardHeader>
          <CardTitle data-testid="settings-gmail-title">Gmail / Google Workspace</CardTitle>
          <CardDescription data-testid="settings-gmail-desc" className="text-zinc-100">
            Connect a mailbox to send emails and detect replies.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-xl border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] p-4">
            <div data-testid="settings-gmail-status" className="text-sm text-zinc-100">Status: {gmailStatus?.connected ? "Connected" : "Not connected"}</div>
            <div data-testid="settings-gmail-email" className="text-sm text-zinc-300">{gmailStatus?.email_address || ""}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button data-testid="settings-gmail-connect" onClick={connectGmail} className="rounded-xl bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 text-white shadow-[0_18px_55px_-35px_rgba(45,212,191,0.75)] hover:from-indigo-400 hover:via-sky-400 hover:to-teal-300">
              Connect Gmail
            </Button>
            <Button data-testid="settings-gmail-disconnect" onClick={disconnectGmail} variant="secondary" className="rounded-xl bg-white/20 text-zinc-100 hover:bg-white/[0.18]">
              Disconnect
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.05))] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-testid="settings-safety-card">
        <CardHeader>
          <CardTitle data-testid="settings-safety-title">Review before send</CardTitle>
          <CardDescription data-testid="settings-safety-desc" className="text-zinc-100">
            You always see a Draft first. You can edit subject and body, then press Send.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};

// ----------------------
// Routing
// ----------------------

function AppRouter({ auth }) {
  const location = useLocation();

  // Process OAuth session callback first
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback onAuthed={(u) => {
      auth.setUser(u);
      auth.setAuthState(true);
    }} />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/*"
        element={
          <Protected auth={auth}>
            <Shell auth={auth}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="import" element={<ImportPage />} />
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="companies/:companyId" element={<CompanyDetailPage />} />
                <Route path="outreach" element={<OutreachPage />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="inbox/:threadId" element={<InboxThreadPage />} />
                <Route path="offers" element={<OffersPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="admin" element={<AdminPage auth={auth} />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Shell>
          </Protected>
        }
      />
    </Routes>
  );
}

export default function App() {
  const auth = useAuth();

  return (
    <BrowserRouter>
      <AppRouter auth={auth} />
    </BrowserRouter>
  );
}
